import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import { FaCalendarAlt } from "react-icons/fa";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { PrimaryButton, GhostButton, DangerButton } from "./components/ThemedButton";
import StatusBadge from "./components/StatusBadge";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtAmt = v => `$${(Number(v)||0).toFixed(2)}`;
// Same "1 hr" / "1.5 hrs" / "30 min" convention LaneBookingAdmin.js uses for duration.
const fmtDuration = mins => {
  const m = Number(mins) || 0;
  if (m >= 60) return `${(m / 60).toFixed(m % 60 === 0 ? 0 : 1)} hr${m !== 60 ? "s" : ""}`;
  return `${m} min`;
};

const STATUS_META = {
  pending_payment: { label:"Pending Payment", tone:"warning" },
  confirmed:       { label:"Confirmed",        tone:"success" },
  checked_in:      { label:"Checked In",       tone:"info" },
  completed:       { label:"Completed",         tone:"info" },
  cancelled:       { label:"Cancelled",         tone:"neutral" },
  no_show:         { label:"No Show",           tone:"warning" },
};

export default function OTOAdminDashboard() {
  const { isDark: T } = useTheme();
  const { fmtDate, fmtTime, fmtDateTime } = useDateTimeFormat();
  const textPrimary   = T ? "rgba(255,255,255,0.88)" : "#1a2a3a";
  const textSecondary = T ? "rgba(255,255,255,0.50)" : "rgba(3,78,123,0.55)";
  const divider       = T ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)";

  // Same plain button styling as POSOrders.js / POSInventory.js's Actions column —
  // one filled color (yellow, for the single primary action per context) plus two
  // outlined styles (neutral ghost, red danger) — kept consistent across the app
  // rather than a different solid-fill color per action.
  const card = {
    background: T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)",
    border: T ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.12)",
    borderRadius: 14, padding: 16, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  };
  const inputSt = {
    padding: "8px 10px", borderRadius: 8, fontSize: 13, outline: "none",
    background: T ? "rgba(255,255,255,0.08)" : "#f4f7fb",
    color: T ? "rgba(255,255,255,0.88)" : "#1a2a3a",
    border: T ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(3,78,123,0.18)",
  };
  const thSt = {
    padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", color: textSecondary,
    borderBottom: `2px solid ${divider}`, whiteSpace: "nowrap",
  };
  const tdSt = { padding: "10px 12px", borderBottom: `1px solid ${divider}` };

  const now = new Date();
  const [month,        setMonth]        = useState(now.getMonth() + 1);
  const [year,         setYear]         = useState(now.getFullYear());
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState("");
  const [statusF,      setStatusF]      = useState("");
  const [cancelId,     setCancelId]     = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelling,   setCancelling]   = useState(false);
  const [refundBooking, setRefundBooking] = useState(null);
  const [refundType,    setRefundType]    = useState("full");
  const [refundAmt,     setRefundAmt]     = useState("");
  const [refundReason,  setRefundReason]  = useState("");
  const [refunding,     setRefunding]     = useState(false);
  const [refundResult,  setRefundResult]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/oto/admin/dashboard?month=${month}&year=${year}`);
      const d = await res.json();
      if (d.success) setData(d);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const confirmManual = async bid => {
    await fetch(`${API_BASE}/oto/bookings/${bid}/confirm-manual`, { method: "POST" });
    load();
  };

  const submitRefund = async () => {
    if (!refundBooking) return;
    const applicable = parseFloat(refundBooking.applicable_refund ?? refundBooking.amount_due ?? 0);
    const partial = parseFloat(refundAmt);
    if (refundType === "partial" && (!refundAmt || isNaN(partial) || partial <= 0 || partial > applicable)) {
      setRefundResult({ ok: false, msg: `Enter a valid amount between $0.01 and $${applicable.toFixed(2)}` }); return;
    }
    if (!refundReason.trim()) { setRefundResult({ ok: false, msg: "Please enter a reason." }); return; }
    setRefunding(true); setRefundResult(null);
    try {
      const res = await fetch(`${API_BASE}/oto/bookings/${refundBooking.booking_id}/refund`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refund_type: refundType, amount: refundType === "partial" ? partial : null, reason: refundReason }),
      });
      const d = await res.json();
      setRefundResult({ ok: d.success, msg: d.message, refundId: d.refund_id, refunded: d.refunded_amount });
      if (d.success) load();
    } catch { setRefundResult({ ok: false, msg: "Network error." }); }
    setRefunding(false);
  };

  const cancelBooking = async () => {
    if (!cancelId) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/oto/bookings/${cancelId}/cancel`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: cancelReason }),
      });
      const d = await res.json();
      setCancelResult({ refund_message: d.refund_message, refund_status: d.refund_status });
      load();
    } catch {
      setCancelResult({ refund_message: "Cancellation failed — please try again.", refund_status: null });
    }
    setCancelling(false);
  };

  const filteredBookings = (data?.bookings || []).filter(b => {
    const name = `${b.first_name} ${b.last_name} ${b.coach_name}`.toLowerCase();
    return (!search || name.includes(search.toLowerCase())) && (!statusF || b.status === statusF);
  });
  const bookingsSort = useSort(filteredBookings, "session_date");
  const bookings = bookingsSort.sorted;

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary, padding: "20px 28px 48px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T ? "#93c5fd" : C.blue }}>
            1-on-1 Admin
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={inputSt}>
            {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={inputSt}>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <GhostButton size="sm" onClick={load}>{loading ? "Loading…" : "Refresh"}</GhostButton>
        </div>
      </div>

      {loading ? (
        <div style={{ color: textSecondary, textAlign: "center", padding: "60px 0" }}>Loading…</div>
      ) : (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>All Bookings ({bookings.length})</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="Search student or coach…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ ...inputSt, width: 200 }} />
              <select value={statusF} onChange={e => setStatusF(e.target.value)} style={inputSt}>
                <option value="">All Statuses</option>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    { label: "#", key: null },
                    { label: "Date", key: "session_date" },
                    { label: "Time", key: "start_time" },
                    { label: "Student", key: "first_name" },
                    { label: "Coach", key: "coach_name" },
                    { label: "Duration", key: "duration_minutes" },
                    { label: "Amount", key: "amount_due" },
                    { label: "Refund", key: "refunded_amount" },
                    { label: "Check-in", key: "checked_in_at" },
                    { label: "Status", key: "status" },
                    { label: "Payment ID", key: "stripe_payment_intent_id" },
                    { label: "Actions", key: null },
                  ].map(({ label: h, key }) => (
                    <th key={h}
                      onClick={key ? () => bookingsSort.toggleSort(key) : undefined}
                      style={{ ...thSt,
                        ...(key ? { cursor: "pointer", userSelect: "none" } : {}),
                        ...(key && bookingsSort.sortKey === key ? { color: getBrandColors().blue } : {}) }}>
                      {h}{key ? bookingsSort.sortArrow(key) : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr><td colSpan={12} style={{ padding: "40px 12px", textAlign: "center", color: textSecondary }}>No bookings found</td></tr>
                ) : bookings.map((b, ri) => {
                  const meta      = STATUS_META[b.status] || { label: b.status, tone: "neutral" };
                  const isCheckedIn = b.status === "checked_in";
                  const rowBg     = ri%2===0 ? "transparent" : (T?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.02)");
                  const amtDue    = parseFloat(b.amount_due || 0);
                  const refunded  = parseFloat(b.refunded_amount || 0);
                  const available = Math.max(0, amtDue - refunded);
                  return (
                    <tr key={b.booking_id} style={{ background: rowBg }}>
                      <td style={{ ...tdSt, color: textSecondary }}>{b.booking_id}</td>
                      <td style={{ ...tdSt, fontWeight: 600 }}>{fmtDate(b.session_date)}</td>
                      <td style={{ ...tdSt, whiteSpace: "nowrap" }}>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}</td>
                      <td style={tdSt}>{b.first_name} {b.last_name}</td>
                      <td style={tdSt}>{b.coach_name}</td>
                      <td style={tdSt}>{fmtDuration(b.duration_minutes)}</td>
                      <td style={{ ...tdSt, fontWeight: 700 }}>{fmtAmt(b.amount_due)}</td>
                      <td style={{ ...tdSt, minWidth: 120 }}>
                        {refunded > 0 ? (
                          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                            <div style={{ color: getBrandColors().red }}>↩ {fmtAmt(refunded)}</div>
                            <div style={{ color: available > 0 ? getBrandColors().green : textSecondary }}>Avail: {fmtAmt(available)}</div>
                          </div>
                        ) : <span style={{ color: textSecondary, fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ ...tdSt, textAlign: "center" }}>
                        {isCheckedIn
                          ? <span style={{ fontSize: 14 }} title={b.checked_in_at ? fmtDateTime(b.checked_in_at) : "Checked in"}>✅</span>
                          : b.status === "completed"
                          ? <span style={{ fontSize: 14 }} title="Completed">✔</span>
                          : <span style={{ color: textSecondary, fontSize: 12 }}>—</span>}
                      </td>
                      <td style={tdSt}>
                        <StatusBadge label={meta.label} tone={meta.tone} />
                      </td>
                      <td style={{ ...tdSt, fontSize: 11, color: textSecondary,
                        maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.stripe_payment_intent_id === "pi_test_placeholder" ? "—" : (b.stripe_payment_intent_id || "—")}
                      </td>
                      <td style={tdSt}>
                        {(() => {
                          const canPay    = b.status === "pending_payment";
                          const canRefund = ["confirmed","checked_in","completed"].includes(b.status)
                            && b.stripe_payment_intent_id && b.stripe_payment_intent_id !== "pi_test_placeholder"
                            && available > 0;
                          const canCancel = !["cancelled","completed","no_show"].includes(b.status);
                          const openRefund = () => {
                            setRefundBooking({ ...b, applicable_refund: available });
                            setRefundType("full"); setRefundAmt(""); setRefundReason(""); setRefundResult(null);
                          };
                          const openCancel = () => { setCancelId(b.booking_id); setCancelReason(""); };
                          return (
                            <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                              <GhostButton size="sm" disabled={!canPay} title={canPay ? "Mark this booking paid" : "Already paid"}
                                onClick={() => canPay && confirmManual(b.booking_id)}>Pay</GhostButton>
                              <GhostButton size="sm" disabled={!canRefund} title={canRefund ? "Issue a refund" : "Nothing left to refund"}
                                onClick={() => canRefund && openRefund()}>↩ Refund</GhostButton>
                              <DangerButton size="sm" disabled={!canCancel} title={canCancel ? "Cancel this booking" : "Already final"}
                                onClick={() => canCancel && openCancel()}>Cancel</DangerButton>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelId && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
          <div style={{ ...card, width:440, padding:28 }}>
            {cancelResult ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>
                  {cancelResult.refund_status==="succeeded"?"✅":cancelResult.refund_status==="pending"?"⏳":"📋"}
                </div>
                <div style={{ fontSize:16, fontWeight:800, marginBottom:10 }}>
                  {cancelResult.refund_status==="succeeded" ? "Cancelled & Refunded"
                   : cancelResult.refund_status==="pending"  ? "Cancelled — Refund Pending"
                   : "Booking Cancelled"}
                </div>
                <div style={{ padding:"10px 14px", borderRadius:8, fontSize:12, marginBottom:18, textAlign:"left",
                  background: cancelResult.refund_status==="succeeded" ? "rgba(16,185,129,0.10)"
                    : cancelResult.refund_status==="pending" ? "rgba(59,130,246,0.10)" : "rgba(245,158,11,0.10)",
                  color: cancelResult.refund_status==="succeeded" ? getBrandColors().green
                    : cancelResult.refund_status==="pending" ? "#2563eb" : "#92400e" }}>
                  {cancelResult.refund_message}
                </div>
                <PrimaryButton style={{ width:"100%" }} onClick={() => { setCancelId(null); setCancelResult(null); setCancelReason(""); }}>Done</PrimaryButton>
              </div>
            ) : (
              <>
                <div style={{ fontSize:16, fontWeight:800, marginBottom:6 }}>Cancel Booking #{cancelId}</div>
                <div style={{ fontSize:12, color:textSecondary, marginBottom:16 }}>
                  If the parent was charged, a Stripe refund will be issued automatically.
                </div>
                <textarea rows={3} placeholder="Reason for cancellation (optional)…"
                  value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                  style={{ width:"100%", boxSizing:"border-box", padding:"9px 11px", borderRadius:8, fontSize:13,
                    background: T?"rgba(255,255,255,0.07)":"#f4f7fb",
                    border: T?"1px solid rgba(255,255,255,0.14)":"1px solid rgba(3,78,123,0.18)",
                    color:textPrimary, outline:"none", resize:"vertical" }}/>
                <div style={{ display:"flex", gap:10, marginTop:16 }}>
                  <DangerButton style={{ flex:1, justifyContent:"center", padding:"9px", fontSize:13 }} onClick={cancelBooking} disabled={cancelling}>
                    {cancelling ? "Cancelling…" : "Confirm Cancel"}
                  </DangerButton>
                  <GhostButton style={{ flex:1, justifyContent:"center", padding:"9px", fontSize:13 }} onClick={() => setCancelId(null)}>Keep Booking</GhostButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {refundBooking && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
          <div style={{ ...card, width:440, padding:28, maxHeight:"90vh", overflowY:"auto" }}>
            {refundResult?.ok ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                <div style={{ fontSize:16, fontWeight:800, marginBottom:8 }}>
                  Refund {refundResult.refunded ? `$${parseFloat(refundResult.refunded).toFixed(2)}` : ""} Issued
                </div>
                <div style={{ padding:"10px 14px", borderRadius:8, fontSize:12, marginBottom:18,
                  background:"rgba(16,185,129,0.10)", color:getBrandColors().green, textAlign:"left" }}>
                  {refundResult.msg}
                  {refundResult.refundId && <div style={{ marginTop:4, fontFamily:"monospace", fontSize:11 }}>ID: {refundResult.refundId}</div>}
                </div>
                <PrimaryButton style={{ width:"100%" }} onClick={() => { setRefundBooking(null); setRefundResult(null); }}>Done</PrimaryButton>
              </div>
            ) : (
              <>
                <div style={{ fontSize:16, fontWeight:800, marginBottom:4 }}>Issue Refund</div>
                <div style={{ fontSize:12, color:textSecondary, marginBottom:12 }}>Booking #{refundBooking.booking_id}</div>
                {(() => {
                  const amt  = parseFloat(refundBooking.amount_due || 0);
                  const prev = parseFloat(refundBooking.refunded_amount || 0);
                  const appl = parseFloat(refundBooking.applicable_refund ?? amt);
                  return (
                    <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                      {[
                        { label:"CHARGED",    val:amt,  color:textSecondary, bg:T?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.06)" },
                        prev>0 && { label:"REFUNDED",   val:prev, color:getBrandColors().red, bg:"rgba(248,113,113,0.10)" },
                        { label:"APPLICABLE", val:appl, color:getBrandColors().green, bg:"rgba(52,211,153,0.10)" },
                      ].filter(Boolean).map(item => (
                        <div key={item.label} style={{ flex:1, borderRadius:8, padding:"8px 12px", textAlign:"center", background:item.bg }}>
                          <div style={{ fontSize:10, color:item.color, marginBottom:2 }}>{item.label}</div>
                          <div style={{ fontWeight:800, color:item.color }}>{fmtAmt(item.val)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {/* Radio-label toggle, matching POSOrders.js's Full/Partial refund choice */}
                <div style={{ display:"flex", gap:16, marginBottom:14 }}>
                  <label style={{ fontSize:12, display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                    <input type="radio" checked={refundType==="full"} onChange={() => setRefundType("full")} />
                    Full ({fmtAmt(refundBooking.applicable_refund ?? refundBooking.amount_due)})
                  </label>
                  <label style={{ fontSize:12, display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                    <input type="radio" checked={refundType==="partial"} onChange={() => setRefundType("partial")} />
                    Partial refund
                  </label>
                </div>
                {refundType==="partial" && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:textSecondary, marginBottom:5, textTransform:"uppercase" }}>Amount</div>
                    <input type="number" min="0.01" step="0.01"
                      max={refundBooking.applicable_refund ?? refundBooking.amount_due}
                      value={refundAmt} onChange={e => setRefundAmt(e.target.value)}
                      placeholder={`Max $${parseFloat((refundBooking.applicable_refund??refundBooking.amount_due)||0).toFixed(2)}`}
                      style={{ ...inputSt, width:"100%", boxSizing:"border-box" }}/>
                  </div>
                )}
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:textSecondary, marginBottom:5, textTransform:"uppercase" }}>Reason *</div>
                  <textarea rows={3} value={refundReason} onChange={e => setRefundReason(e.target.value)}
                    placeholder="Brief description of why the refund is being issued…"
                    style={{ ...inputSt, width:"100%", boxSizing:"border-box", resize:"vertical" }}/>
                </div>
                {refundResult && !refundResult.ok && (
                  <div style={{ fontSize:12, color:getBrandColors().red, background:"rgba(220,38,38,0.07)",
                    border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, padding:"8px 12px", marginBottom:14 }}>
                    {refundResult.msg}
                  </div>
                )}
                <div style={{ display:"flex", gap:10 }}>
                  <PrimaryButton style={{ flex:2, padding:"9px", fontSize:13, textAlign:"center" }} onClick={submitRefund} disabled={refunding}>
                    {refunding ? "Processing…" : "Confirm Refund"}
                  </PrimaryButton>
                  <GhostButton style={{ flex:1, justifyContent:"center", padding:"9px", fontSize:13 }} onClick={() => { setRefundBooking(null); setRefundResult(null); }}>Cancel</GhostButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
